import React, { useState, useRef, useEffect } from "react";
import useEffectOnce from "../../useEffectOnce";
import "./Publisher.scss";
import { Radio, RadioGroup, FormControlLabel } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LogicModal from "../../Components/LogicModal/LogicModal";
import AiConfidenceModal from "../../Components/AiConfidenceModal/AiConfidenceModal";
import { serverAddr } from "../../networkConfig";
import SearchCoin from "../../Components/SearchCoin/SearchCoin";


const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return timestamp;
    const formatted = date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata',
    });
    return `${formatted} IST`;
  } catch {
    return timestamp;
  }
};

// Target limits are always based on minimum stoploss distance (0.3% of entry),
// T1/T2 = Entry ± MAX(minRisk * RR, Entry * minPct)
const calculateTargetLimits = (entry, opinion, entryRange1, stoploss, entryRange2) => {
  const e = Number(entry);
  if (!e) return { t1Limit: null, t2Limit: null };

  // Minimum stoploss = 0.3% of entry
  const minRisk = e * 0.003;

  if (opinion === "long") {
    // Long: T1 = Entry2_upper × (1 + MAX(slPct×1.01, 1.5%))
    //       T2 = Entry2_upper × (1 + MAX(slPct×1.51, 2.5%))
    const er2 = Number(entryRange2);
    const sl = Number(stoploss);
    if (er2 && sl) {
      const slPctDecimal = (er2 - sl) / er2;
      const t1Min = er2 * (1 + Math.max(slPctDecimal * 1.01, 0.015));
      const t2Min = er2 * (1 + Math.max(slPctDecimal * 1.51, 0.025));
      return {
        t1Limit: parseFloat(t1Min.toFixed(4)),
        t2Limit: parseFloat(t2Min.toFixed(4))
      };
    }
    // Fallback when entryRange2/stoploss not yet entered
    const t1Min = Math.max(e + minRisk, e * 1.015);
    const t2Min = Math.max(e + minRisk * 1.5, e * 1.025);
    return {
      t1Limit: parseFloat(t1Min.toFixed(4)),
      t2Limit: parseFloat(t2Min.toFixed(4))
    };
  } else {
    // Short: T1 = Entry1_lower × (1 − MAX(slPct×1.01, 1.5%))
    //        T2 = Entry1_lower × (1 − MAX(slPct×1.51, 2.5%))
    const er1 = Number(entryRange1);
    const sl = Number(stoploss);
    if (er1 && sl) {
      const slPctDecimal = (sl - er1) / er1;
      const t1Max = er1 * (1 - Math.max(slPctDecimal * 1.01, 0.015));
      const t2Max = er1 * (1 - Math.max(slPctDecimal * 1.51, 0.025));
      return {
        t1Limit: parseFloat(t1Max.toFixed(4)),
        t2Limit: parseFloat(t2Max.toFixed(4))
      };
    }
    // Fallback when entryRange1/stoploss not yet entered
    const t1Max = e - Math.max(minRisk, e * 0.015);
    const t2Max = e - Math.max(minRisk * 1.5, e * 0.025);
    return {
      t1Limit: parseFloat(t1Max.toFixed(4)),
      t2Limit: parseFloat(t2Max.toFixed(4))
    };
  }
};

// Stoploss range — always returned as { min: lower_number, max: higher_number }
//   slClose = Entry ± 0.3%              — closest allowed SL to entry 
//   slFar   = Entry * (1 ± 0.9/Lev)    — furthest allowed SL from entry
// Long:  both below entry → slFar < slClose  → { min: slFar,  max: slClose }
// Short: both above entry → slClose < slFar  → { min: slClose, max: slFar  }
const calculateStoplossRange = (entry, opinion, leverage) => {
  if (!entry) return { min: "", max: "" };
  const e = Number(entry);
  const lev = Number(leverage);

  const slClose = opinion === "long"
    ? parseFloat((e * 0.997).toFixed(4))   // 0.3% below entry
    : parseFloat((e * 1.003).toFixed(4));  // 0.3% above entry

  if (!lev) {
    // Without leverage: Long only has max (closest), Short only has min (closest)
    return opinion === "long"
      ? { min: "", max: slClose }
      : { min: slClose, max: "" };
  }

  const factor = Math.min(0.9 / lev, 20 / lev);
  const slFar = opinion === "long"
    ? parseFloat((e * (1 - factor)).toFixed(4))  // further below entry
    : parseFloat((e * (1 + factor)).toFixed(4)); // further above entry

  // Always numerically ordered: min < max
  return opinion === "long"
    ? { min: slFar, max: slClose }   // slFar < slClose for Long
    : { min: slClose, max: slFar };  // slClose < slFar for Short
};

// Entry range hard limits — same formula for Long and Short
// Entry1 (lower) = Entry - MAX(0.5% × Entry, 5% × |SL1 - T1limit|)
// Entry2 (upper) = Entry + 1.5% × Entry
// User may tighten within these bounds but cannot go outside them.
const calculateEntryRangeLimits = (entry, opinion) => {
  if (!entry) return { hardMin: "", hardMax: "" };
  const e = Number(entry);
  const sl1 = opinion === "long" ? e * 0.997 : e * 1.003;
  const minRisk = e * 0.003;
  const t1Limit = opinion === "long"
    ? Math.max(e + minRisk, e * 1.015)
    : e - Math.max(minRisk, e * 0.015);
  const hardMin = parseFloat((e - Math.max(0.005 * e, 0.05 * Math.abs(sl1 - t1Limit))).toFixed(4));
  const hardMax = parseFloat((e + 0.015 * e).toFixed(4));
  return { hardMin, hardMax };
};

const getTodayStr = () => new Date().toISOString().split('T')[0];
const getMaxDateStr = () => {
  const d = new Date();
  d.setDate(d.getDate() + 90);
  return d.toISOString().split('T')[0];
};
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, day] = dateStr.split('-');
  return `${day}/${m}/${y}`;
};

function Card({ coin, setCoins, index }) {
  const [opinion, setOpinion] = useState("long");
  const [entryType, setEntryType] = useState("market");

  const [entry, setEntry] = useState("");
  const [target1, setTarget1] = useState("");
  const [target2, setTarget2] = useState("");
  const [stoploss, setStoploss] = useState("");
  const [leverage, setLeverage] = useState("");
  const [validity, setValidity] = useState("");
  const [entryRange1, setEntryRange1] = useState("");
  const [entryRange2, setEntryRange2] = useState("");
  const [openLogic, setOpenLogic] = useState(false);
  const [openAiScore, setOpenAiScore] = useState(false);
  const [errors, setErrors] = useState({});
  const [isPublished, setIsPublished] = useState(false);
  const dateInputRef = useRef(null);

  const { hardMin, hardMax } = calculateEntryRangeLimits(Number(entry), opinion);

  const { t1Limit, t2Limit } = calculateTargetLimits(entry, opinion, entryRange1, stoploss, entryRange2);

  const { min: slRangeMin, max: slRangeMax } = calculateStoplossRange(Number(entry), opinion, Number(leverage));

  // Reset entry range to hard limits whenever entry or opinion changes
  useEffect(() => {
    if (hardMin !== "") setEntryRange1(String(hardMin));
    if (hardMax !== "") setEntryRange2(String(hardMax));
  }, [entry, opinion]);

  // RR calculation
  const calculateRR = (entry, stoploss, target1, opinion) => {
    const risk = Math.abs(entry - stoploss);
    if (risk === 0) return 0;

    if (opinion === "long") {
      return (target1 - entry) / risk;
    } else {
      return (entry - target1) / risk;
    }
  };

  // Entry validation (±10%)
  const validateEntry = (entry, markPrice) => {
    const min = markPrice * 0.9;
    const max = markPrice * 1.1;

    return entry >= min && entry <= max;
  };

  // Stoploss validation: must be within [min, max] range
  const validateStoploss = (entry, stoploss, opinion, leverage) => {
    const { min, max } = calculateStoplossRange(entry, opinion, leverage);
    if (!min && !max) return true;
    if (!min) return stoploss <= max;  // Long without leverage: only upper bound
    if (!max) return stoploss >= min;  // Short without leverage: only lower bound
    return stoploss >= min && stoploss <= max;
  };

  const validateAll = ({
    entry,
    markPrice,
    stoploss,
    target1,
    target2,
    opinion,
    entryRange1,
    entryRange2,
    leverage,
    backendLeverage
  }) => {
    const errors = {};

    // LEVERAGE VALIDATION
    const lev = Number(leverage);
    if (leverage && (isNaN(lev) || lev < 2)) {
      errors.leverage = "Leverage must be at least 2x";
    } else if (leverage && backendLeverage && lev > Number(backendLeverage)) {
      errors.leverage = `Leverage cannot exceed ${backendLeverage}x (exchange limit)`;
    }

    if (!entry || !stoploss || !target1 || !target2) {
      errors.general = "All fields are required";
      return { errors, final: "REJECTED" };
    }

    // ENTRY VALIDATION (±10% of mark price)
    const minEntry = markPrice * 0.9;
    const maxEntry = markPrice * 1.1;

    if (entry < minEntry || entry > maxEntry) {
      errors.entry = `Entry must be between ${minEntry.toFixed(2)} – ${maxEntry.toFixed(2)}`;
    }

    // STOPLOSS VALIDATION — must fall within [slMin, slMax]
    const { min: slMin, max: slMax } = calculateStoplossRange(entry, opinion, leverage);

    if (opinion === "long" && stoploss >= entry) {
      errors.stoploss = `Stoploss must be below entry. Valid range: ${slMin} – ${slMax}`;
    } else if (opinion === "short" && stoploss <= entry) {
      errors.stoploss = `Stoploss must be above entry. Valid range: ${slMin} – ${slMax}`;
    } else if ((slMin !== "" && stoploss < slMin) || (slMax !== "" && stoploss > slMax)) {
      errors.stoploss = `Stoploss must be between ${slMin} – ${slMax}`;
    }

    // RR CALCULATION
    const rr = calculateRR(entry, stoploss, target1, opinion);

    // ENTRY RANGE VALIDATION — user values must stay within hard limits
    const { hardMin, hardMax } = calculateEntryRangeLimits(entry, opinion);
    const er1 = Number(entryRange1);
    const er2 = Number(entryRange2);

    if (er1 && er2) {
      if (er1 < hardMin) {
        errors.entryRange = `Entry 1 (lower) cannot be below ${hardMin}`;
      } else if (er2 > hardMax) {
        errors.entryRange = `Entry 2 (upper) cannot be above ${hardMax}`;
      } else if (er1 >= er2) {
        errors.entryRange = "Entry 1 must be less than Entry 2";
      }
    }

    // TARGET DIRECTION VALIDATION
    if (opinion === "long") {
      if (target1 <= entry) {
        errors.target1 = "Target 1 must be above entry (LONG)";
      }
      if (target2 <= target1) {
        errors.target2 = "Target 2 must be above Target 1 (LONG)";
      }
    } else {
      if (target1 >= entry) {
        errors.target1 = "Target 1 must be below entry (SHORT)";
      }
      if (target2 >= target1) {
        errors.target2 = "Target 2 must be below Target 1 (SHORT)";
      }
    }

    // TARGET RR VALIDATION
    const { t1Limit: t1Lim, t2Limit: t2Lim } = calculateTargetLimits(entry, opinion, entryRange1, stoploss, entryRange2);

    if (t1Lim !== null && !errors.target1) {
      if (opinion === "long") {
        if (target1 < t1Lim) {
          errors.target1 = `Target 1 must be ≥ ${t1Lim} (min RR 1:1 or 1.5%)`;
        }
      } else {
        if (target1 > t1Lim) {
          errors.target1 = `Target 1 must be ≤ ${t1Lim} (min RR 1:1 or 1.5%)`;
        }
      }
    }

    if (t2Lim !== null && !errors.target2) {
      if (opinion === "long") {
        if (target2 < t2Lim) {
          errors.target2 = `Target 2 must be ≥ ${t2Lim} (min RR 1.5:1 or 2.5%)`;
        }
      } else {
        if (target2 > t2Lim) {
          errors.target2 = `Target 2 must be ≤ ${t2Lim} (min RR 1.5:1 or 2.5%)`;
        }
      }
    }

    return {
      errors,
      final: Object.keys(errors).length === 0 ? "APPROVED" : "REJECTED",
      rr
    };
  };

  useEffect(() => {
    const e = Number(entry);
    const mp = Number(coin.markPrice);
    const sl = Number(stoploss);
    const t1 = Number(target1);
    const t2 = Number(target2);
    const lev = Number(leverage);

    // Don't show any errors until user has started filling fields
    if (!e && !sl && !t1 && !t2) {
      setErrors({});
      return;
    }

    const newErrors = {};

    if (e && mp && !validateEntry(e, mp)) {
      const minEntry = parseFloat((mp * 0.9).toFixed(2));
      const maxEntry = parseFloat((mp * 1.1).toFixed(2));
      newErrors.entry = `Entry must be between ${minEntry} – ${maxEntry}`;
    }

    if (e && sl && !validateStoploss(e, sl, opinion, lev)) {
      const { min: slMin, max: slMax } = calculateStoplossRange(e, opinion, lev);
      const rangeStr = slMin !== "" && slMax !== "" ? `${slMin} – ${slMax}` : slMin !== "" ? `≥ ${slMin}` : `≤ ${slMax}`;
      newErrors.stoploss = `Stoploss must be ${rangeStr}`;
    }

    if (e && sl && t1 && t2) {
      const result = validateAll({
        entry: e,
        markPrice: mp,
        stoploss: sl,
        target1: t1,
        target2: t2,
        entryRange1,
        entryRange2,
        opinion,
        leverage: lev,
        backendLeverage: coin.backendLeverage
      });
      const { general, ...fieldErrors } = result.errors;
      setErrors(fieldErrors);
    } else {
      setErrors(newErrors);
    }
  }, [entry, stoploss, target1, target2, opinion, leverage, entryRange1, entryRange2, coin.markPrice]);

  const handleLogicClick = () => {
    const e = Number(entry);
    const mp = Number(coin.markPrice);
    const sl = Number(stoploss);
    const t1 = Number(target1);
    const t2 = Number(target2);
    const lev = Number(leverage);

    if (!e || !sl || !t1 || !t2 || !lev || !validity) {
      setErrors({
        general: "All fields are required",
        ...(!validity && { validity: "Validity date is required" })
      });
      return;
    }

    const result = validateAll({
      entry: e,
      markPrice: mp,
      stoploss: sl,
      target1: t1,
      target2: t2,
      entryRange1,
      entryRange2,
      opinion,
      leverage: lev,
      backendLeverage: coin.backendLeverage
    });

    if (result.final === "REJECTED") {
      setErrors(result.errors);
      return;
    }

    setErrors({});
    setOpenLogic(true);
  };


  return (
    <div className="publisher-card">
      <div className="card-header">
        <div className="coin-name">{coin.name}</div>

        <div className="price">
          ${coin.price}
          <span className="green"> ▲{coin.change}</span>
        </div>

        <div className="date">{coin.date}</div>
      </div>

      <div className="card-divider"></div>


      <div className="form-rows">

        <div className="form-row">
          <div className="form-left form-group">
            <label>My Opinion *</label>
            <div className="toggle">
              <button
                className={opinion === "long" ? "active" : ""}
                onClick={() => setOpinion("long")}
              >
                Long
              </button>
              <button
                className={opinion === "short" ? "active" : ""}
                onClick={() => setOpinion("short")}
              >
                Short
              </button>
            </div>
          </div>
          <div className="form-right form-group" style={{ marginBottom: '10px' }}>
            <label>Mark Price *</label>
            <input
              className=" entry-input"
              placeholder="Enter Mark Price"
              value={coin.markPrice || ""}
              onChange={(e) => {
                const value = e.target.value;

                setCoins((prev) => {
                  const updated = [...prev];
                  updated[index] = {
                    ...updated[index],
                    markPrice: value
                  };
                  return updated;
                });
              }}
            />
          </div>

        </div>

        <div className="form-row">
          <div className="form-left form-group">
            <label>Validity *</label>
            <div className="date-input-wrap" onClick={() => dateInputRef.current?.showPicker()}>
              {validity
                ? <span className="date-formatted">{formatDateDisplay(validity)}</span>
                : <span className="date-placeholder">Select Date</span>
              }
              <input
                ref={dateInputRef}
                type="date"
                value={validity}
                min={getTodayStr()}
                max={getMaxDateStr()}
                onChange={(e) => setValidity(e.target.value)}
              />
              <CalendarTodayIcon className="date-icon" />
            </div>
            {errors.validity && <div className="error-text">{errors.validity}</div>}
          </div>

          <div className="form-right form-group">
            <label>Entry Price *</label>
            <div className="entry-row">
              <input
                className="entry-price-input"
                placeholder="LTP"
                value={entry}
                onChange={(e) => setEntry(e.target.value)}
              />
              <RadioGroup
                row
                value={entryType}
                onChange={(e) => setEntryType(e.target.value)}
                className="entry-radio"
              >
                <FormControlLabel
                  value="market"
                  control={<Radio size="small" />}
                  label="Market"
                />
                <FormControlLabel
                  value="limit"
                  control={<Radio size="small" />}
                  label="Limit"
                />
              </RadioGroup>
            </div>
            <div className="sub-text">Must be within ±10% of mark price</div>
            {errors.entry && <div className="error-text">{errors.entry}</div>}
          </div>


        </div>

        <div className="form-row">
          <div className="form-left form-group">
            <label>Leverage *</label>
            <input
              placeholder="0X"
              value={leverage}
              onChange={(e) => setLeverage(e.target.value)}
            />
            {errors.leverage && <div className="error-text">{errors.leverage}</div>}
            <span className="sub-text">
              {coin.backendLeverage
                ? `Range: 2x – ${coin.backendLeverage}x`
                : "Min: 2x"}
            </span>
          </div>

          <div className="form-right form-group">
            <label>Entry Range *</label>
            <div className="range-row">
              <div className="input-box">
                <span className="input-box__prefix">₹</span>
                <input
                  placeholder="Lower"
                  value={entryRange1}
                  onChange={(ev) => setEntryRange1(ev.target.value)}
                />
              </div>
              <div className="input-box">
                <span className="input-box__prefix">₹</span>
                <input
                  placeholder="Upper"
                  value={entryRange2}
                  onChange={(ev) => setEntryRange2(ev.target.value)}
                />
              </div>
            </div>
            {entry ? (
              <span className="sub-text">Limits: {hardMin} – {hardMax}</span>
            ) : (
              <span className="sub-text">Auto-calculated from entry</span>
            )}
            {errors.entryRange && <div className="error-text">{errors.entryRange}</div>}
          </div>

        </div>

        <div className="form-row">
          <div className="form-left form-group">
            <label>Stoploss *</label>
            <div className="input-box">
              <span className="input-box__prefix">₹</span>
              <input
                placeholder="200"
                value={stoploss}
                onChange={(e) => setStoploss(e.target.value)}
              />
              <span className="input-box__suffix">
                {stoploss && (opinion === "long" ? entryRange2 : entryRange1)
                  ? opinion === "long"
                    ? `${parseFloat(((Number(entryRange2) - Number(stoploss)) / Number(entryRange2) * 100).toFixed(4))}%`
                    : `${parseFloat(((Number(stoploss) - Number(entryRange1)) / Number(entryRange1) * 100).toFixed(4))}%`
                  : "0%"}
              </span>
            </div>
            <span className="sub-text">
              {entry && leverage
                ? `Range: ${slRangeMin} – ${slRangeMax}`
                : entry
                  ? opinion === "long"
                    ? `Max: ${slRangeMax} (enter leverage for min)`
                    : `Min: ${slRangeMin} (enter leverage for max)`
                  : "Must be within 0.3% – 20%(leveraged) of Entry"}
            </span>
            {errors.stoploss && <div className="error-text">{errors.stoploss}</div>}
          </div>
          <div className="form-right form-group">
            <div className="targets-row">
              <div className="form-group">
                <label>Target 1 *</label>
                <div className="input-box">
                  <span className="input-box__prefix">₹</span>
                  <input
                    placeholder="100"
                    value={target1}
                    disabled={!stoploss}
                    onChange={(e) => setTarget1(e.target.value)}
                  />
                  <span className="input-box__suffix">
                    {target1 && entry
                      ? opinion === "long"
                        ? `${parseFloat(((Number(target1) - Number(entry)) / Number(entry) * 100).toFixed(4))}%`
                        : `${parseFloat(((Number(entry) - Number(target1)) / Number(entry) * 100).toFixed(4))}%`
                      : "0%"}
                  </span>
                </div>
                <span className="sub-text">
                  RR- 1:1; min. 1.5%{t1Limit !== null ? ` | ${opinion === "long" ? "Min" : "Max"}: ${t1Limit}` : ""}
                </span>
                {!stoploss && <div className="error-text">Add stoploss first</div>}
                {stoploss && errors.target1 && <div className="error-text">{errors.target1}</div>}
              </div>

              <div className="form-group">
                <label>Target 2 *</label>
                <div className="input-box">
                  <span className="input-box__prefix">₹</span>
                  <input
                    placeholder="90"
                    value={target2}
                    disabled={!stoploss}
                    onChange={(e) => setTarget2(e.target.value)}
                  />
                  <span className="input-box__suffix">
                    {target2 && entry
                      ? opinion === "long"
                        ? `${parseFloat(((Number(target2) - Number(entry)) / Number(entry) * 100).toFixed(4))}%`
                        : `${parseFloat(((Number(entry) - Number(target2)) / Number(entry) * 100).toFixed(4))}%`
                      : "0%"}
                  </span>
                </div>
                <span className="sub-text">
                  RR-1.5:1; min. 2.5%{t2Limit !== null ? ` | ${opinion === "long" ? "Min" : "Max"}: ${t2Limit}` : ""}
                </span>
                {!stoploss && <div className="error-text">Add stoploss first</div>}
                {stoploss && errors.target2 && <div className="error-text">{errors.target2}</div>}
              </div>
            </div>
          </div>
        </div>

      </div>

      <div className="footer-info">
        Scalping, defined as a very short-duration trade setup designed
        to capture small price movements within minutes to a few hours,
        typically targeting low percentage gains with tight stop-loss
        levels, is strictly not permitted on CryptoAnts
      </div>

      {errors.general && (
        <div className="error-text" style={{ textAlign: "center", marginTop: '20px', marginBottom: "10px" }}>
          {errors.general}
        </div>
      )}
      <div className="card-footer">
        <button
          className={`ai-score-btn${isPublished ? " published-btn" : ""}`}
          onClick={isPublished ? undefined : handleLogicClick}
          disabled={isPublished}
        >
          {isPublished ? "Published" : "LOGIC"}
        </button>
      </div>
      <LogicModal
        open={openLogic}
        onClose={() => setOpenLogic(false)}
        onPublishSuccess={() => setIsPublished(true)}
        opinion={opinion}
        validity={validity}
        scripId={coin.id}
        entry={entry}
        target1={target1}
        target2={target2}
        stoploss={stoploss}
        leverage={leverage}
        entryRange1={entryRange1}
        entryRange2={entryRange2}
      />
      <AiConfidenceModal
        open={openAiScore}
        onClose={() => setOpenAiScore(false)}
        onAddLogic={() => { setOpenAiScore(false); setOpenLogic(true); }}
        score={coin.confidenceScore}
      />
    </div>
  );
}

export default function Publisher() {
  const [coinsData, setCoins] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleSearchResult = (coin) => {
    console.log(coin);

    setCoins((prev) => {
      // remove duplicate if already exists
      const filtered = prev.filter((c) => c.name !== coin.name);

      // add searched coin at top
      return [coin, ...filtered];
    });
  };

  useEffectOnce(() => {
    fetchCoins();
  }, []);

  const fetchCoins = async () => {
    try {
      setLoading(true);

      const response = await serverAddr.post("/publisher/get_crypto_coins_details/", {
        page_no: 1,
        page_per_entries: 6
      });

      const data = response?.data || [];

      const formatted = data.map((item) => {
        return {
          id: item.id,
          name: item.title || '',
          price: item.price,
          change: item.price_change,
          date: formatTimestamp(item.timestamp),
          confidenceScore: item.confidence_score || 2,
          markPrice: null,
          backendLeverage: item.leverage || null
        };
      });

      setCoins(formatted);

    } catch (error) {
      console.error("Error fetching coins:", error);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="publisher-wrapper">
      <div className="search-bar">
        <SearchCoin onSelect={handleSearchResult} />
      </div>

      <div className="publisher-container">
        {coinsData.map((coin, index) => (
          <Card key={index} coin={coin} setCoins={setCoins} index={index} />
        ))}
      </div>
    </div>
  );
}